import * as React from 'react';

import { useLatestRef } from '@gitbook/util-react-hooks';

import { InteractionLocks, useFirstResponderInteraction } from '../Interactive';

/**
 * Listen to click outside specific elements.
 */
export function useOnClickOutside(
    getElements: () => HTMLElement[],
    callback: ((event: MouseEvent | TouchEvent) => void) | undefined,
    options: { capture: boolean } = { capture: true }
) {
    const callbackRef = useLatestRef(callback);
    const getElementsRef = useLatestRef(getElements);

    const isFirstResponder = useFirstResponderInteraction(
        InteractionLocks.OUTSIDE_CLICK,
        !!callback
    );

    React.useEffect(() => {
        if (!isFirstResponder) {
            return;
        }

        const onMouseDown = (event: MouseEvent | TouchEvent) => {
            if (!callbackRef.current || event.defaultPrevented) {
                return;
            }

            const elements = getElementsRef.current();

            if (
                elements.length === 0 ||
                ('button' in event && event.button && event.button !== 0)
            ) {
                // Not a click, or box not mounted
                return;
            }

            const target = event.target as Node;

            if (elements.some((el) => el.contains(target))) {
                // Clicked on an element inside
                return;
            }

            callbackRef.current(event);
        };

        document.addEventListener('mousedown', onMouseDown, options.capture);
        document.addEventListener('touchstart', onMouseDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown, options.capture);
            document.removeEventListener('touchstart', onMouseDown);
        };
    }, [isFirstResponder, getElementsRef, callbackRef, options.capture]);
}
